import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED_ACCENT_URL = 'https://customer-assets.emergentagent.com/job_idea-signals/artifacts/r9thbfsf_showyn_red_icon_transparent.png';

// =====================================================
// FORM CONFIGURATIONS — Editorial prompts + guidance
// =====================================================
const FORMS = {
  WAITLIST: {
    title: 'Join the Waitlist',
    intro: null,
    isSimple: true,
    fields: [
      { name: 'interest', label: 'What draws you to SHOW\'YN?', hint: 'Select the area that resonates most with where you are right now.', type: 'select', options: ['Ideas', 'Business opportunities', 'Strategy', 'All of the above'] },
      { name: 'industry', label: 'What space or industry are you connected to?', hint: 'Example: health, finance, beauty, education, digital products, services...', type: 'textarea', placeholder: 'Your industry or area of interest' },
    ]
  },
  TRANSITION: {
    title: 'Start Where You Are',
    intro: "You're not starting over. You're repositioning.",
    isSimple: false,
    fields: [
      { name: 'current_phase', label: 'What feels out of alignment right now?', hint: 'Select everything that applies. Honesty here creates clarity later.', type: 'checkbox', options: ['Work', 'Income', 'Direction', 'Lifestyle', 'Everything'] },
      { name: 'emotional_state', label: 'What do you know needs to change, even if you don\'t know how yet?', hint: 'Write freely. This is a guided reflection, not a test.', type: 'textarea', placeholder: 'Example: I need to feel more aligned with what I do every day' },
      { name: 'blocker', label: 'What is holding you back?', hint: 'Identify the resistance. Naming it is the first step to moving through it.', type: 'checkbox', options: ['Fear', 'Uncertainty', 'Lack of clarity', 'Too many options', 'Financial pressure', 'All of the above'] },
      { name: 'desired_direction', label: 'If nothing changes, what do you feel your life will look like in the next year?', hint: 'Be specific. This answer shapes what we send you next.', type: 'textarea', placeholder: 'Example: stuck in the same routine, wanting more but unsure how' },
    ]
  },
  DISCOVERY: {
    title: 'Understand Your Next Move',
    intro: "Not having the idea doesn't mean you're lost. It means you're still listening.",
    isSimple: false,
    fields: [
      { name: 'feels_unclear', label: 'What feels unclear right now?', hint: 'Describe in your own words. There are no wrong answers here.', type: 'textarea', placeholder: 'Example: what to build, where to start, how to choose a direction' },
      { name: 'desired_direction', label: 'What do you want your next phase to feel like?', hint: 'Not what to do. How you want it to feel.', type: 'textarea', placeholder: 'Example: freedom, stability, purpose, creative control' },
      { name: 'challenge', label: 'What is your biggest challenge right now?', hint: 'Be honest. This helps us match you with the right resources.', type: 'radio', options: ['Lack of direction', 'Too many ideas', 'Fear of starting', 'No support system', 'Financial uncertainty'] },
      { name: 'support_level', label: 'What kind of support would help you most?', hint: 'Choose the one that feels most relevant today.', type: 'radio', options: ['Clarity and reflection', 'A structured plan', 'Direct mentorship', 'Community and accountability'] },
    ]
  },
  IDEA_READY: {
    title: 'Turn Your Moment Into Direction',
    intro: "You're not starting from zero. You're starting from something that already exists.",
    isSimple: false,
    fields: [
      { name: 'idea_stage', label: 'Where is your idea right now?', hint: 'Select the stage that best describes your current reality.', type: 'radio', options: ['Not structured yet', 'Clear but not executed', 'Started but not working', 'Early concept'] },
      { name: 'idea_description', label: 'Describe your idea, even if it\'s still unclear', hint: 'Example: "A platform for connecting cleaners with short-term rentals"', type: 'textarea', placeholder: 'What you\'re building, who it\'s for, the problem it solves' },
      { name: 'main_objective', label: 'What would real progress look like in the next 3-6 months?', hint: 'Think outcome, not task.', type: 'radio', options: ['Launch something', 'Gain clarity on direction', 'Generate income', 'Validate the idea'] },
      { name: 'challenge', label: 'Where do you feel most stuck?', hint: 'Select all that apply. These shape the path we build for you.', type: 'checkbox', options: ['Clarity', 'Direction', 'Structure', 'Execution', 'Marketing'] },
      { name: 'investment_ready', label: 'Are you ready to invest in structuring this?', hint: 'This helps us understand your commitment level.', type: 'radio', options: ['Yes, ready now', 'Open to it', 'Not yet'] },
      { name: 'timeline', label: 'What is your timeline?', hint: 'When do you want to see results?', type: 'radio', options: ['Immediately', '1-3 months', '3-6 months', 'No rush'] },
    ]
  },
  IDEA_SUBMISSION: {
    title: 'Submit Your Idea',
    intro: "Every idea is a signal. Some become businesses. Others become direction.",
    isSimple: false,
    fields: [
      { name: 'idea_description', label: 'Describe your idea in 1-2 sentences', hint: 'What it is, who it serves, the problem it solves.', type: 'textarea', placeholder: 'Example: an app that helps freelancers manage client onboarding' },
      { name: 'depth_level', label: 'What stage are you in?', hint: 'Select the one that best reflects where you are today.', type: 'radio', options: ['Concept', 'Early stage', 'Started', 'Growing'] },
      { name: 'investment_ready', label: 'Are you ready to invest in building this?', hint: 'Investment can mean time, money, or both.', type: 'radio', options: ['Yes', 'Maybe', 'Not yet'] },
    ]
  },
  PREMIUM: {
    title: 'Request Guidance',
    intro: "This is where ideas become decisions.",
    isSimple: false,
    fields: [
      { name: 'service_interest', label: 'What do you need help with right now?', hint: 'Be specific. The more detail, the better we can prepare.', type: 'textarea', placeholder: 'Example: positioning my brand, structuring a launch, building a strategy' },
      { name: 'urgency_level', label: 'How urgent is this for you?', hint: 'This helps us prioritize your request.', type: 'radio', options: ['ASAP', '1-3 months', 'Exploring options'] },
      { name: 'budget_range', label: 'What level of investment are you considering?', hint: 'Transparency helps us match you with the right service tier.', type: 'radio', options: ['Under $500', '$500-$2,000', '$2,000-$5,000', '$5,000+', 'Not sure yet'] },
    ]
  }
};

const AGE_OPTIONS = ['18-24', '25-34', '35-44', '45+'];
const GENDER_OPTIONS = ['Female', 'Male', 'Non-binary', 'Prefer not to say', 'Other'];

// =====================================================
// HELPERS
// =====================================================
const isBlank = (v) => {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') {
    const cleaned = v.trim().toLowerCase();
    return !cleaned || ['na', 'n/a', '-', 'none', '...'].includes(cleaned);
  }
  if (Array.isArray(v)) return v.length === 0;
  return false;
};

const cleanPayload = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (Array.isArray(v)) {
      if (v.length > 0) out[k] = v.join(', ');
    } else if (!isBlank(v)) {
      out[k] = v;
    }
  }
  return out;
};

// =====================================================
// FIELD COMPONENTS
// =====================================================
const RadioGroup = ({ options, value, onChange, name }) => (
  <div className="space-y-2">
    {options.map(opt => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`w-full text-left px-5 py-3.5 border transition-all duration-200 font-heading text-base
          ${value === opt
            ? 'bg-black text-white border-black'
            : 'border-neutral-200 text-neutral-700 hover:border-neutral-400'}`}
        data-testid={`${name}-option-${opt.toLowerCase().replace(/[\s/$+,]+/g, '-')}`}
      >
        {opt}
      </button>
    ))}
  </div>
);

const CheckGroup = ({ options, value = [], onChange, name }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(opt => {
      const selected = value.includes(opt);
      return (
        <button
          key={opt}
          type="button"
          onClick={() => {
            if (selected) onChange(value.filter(v => v !== opt));
            else onChange([...value, opt]);
          }}
          className={`px-4 py-2.5 border font-mono text-xs uppercase tracking-wider transition-all duration-200
            ${selected
              ? 'bg-black text-white border-black'
              : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}
          data-testid={`${name}-tag-${opt.toLowerCase().replace(/[\s/$+,]+/g, '-')}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const stepAnim = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

// =====================================================
// SUCCESS SCREEN
// =====================================================
const SuccessScreen = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="text-center py-4">
    <img src={RED_ACCENT_URL} alt="" className="h-10 w-auto mx-auto mb-6" />
    <h3 className="font-heading text-2xl md:text-3xl mb-6">Your direction has been received.</h3>
    <p className="font-heading text-lg italic text-neutral-600 mb-8">
      You'll receive a personalised overview with insights, ideas, and a suggested path for 2026.
    </p>
    <div className="mt-6 pt-6 border-t border-editorial-border">
      <p className="font-heading text-lg text-neutral-800">This is not just a response.</p>
      <p className="font-heading text-xl italic text-black mt-1">It's the beginning of your next move.</p>
    </div>
  </motion.div>
);

// =====================================================
// MAIN COMPONENT
// =====================================================
const EditorialFormModal = ({ formType, isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const [globalData, setGlobalData] = useState({
    name: '', email: '', age_range: '', gender: '',
    nationality: '', country: '', state: '', city: '', industry: ''
  });
  const [responses, setResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setGlobalData({ name: '', email: '', age_range: '', gender: '', nationality: '', country: '', state: '', city: '', industry: '' });
      setResponses({});
      setIsSuccess(false);
      setError('');
    }
  }, [isOpen, formType]);

  if (!formType || !FORMS[formType]) return null;

  const config = FORMS[formType];
  const isSimple = config.isSimple;
  // Simple (waitlist) = 2 steps: identity+location, then form questions
  // Full forms = 3 steps: identity, profile+location, questions
  const totalSteps = isSimple ? 2 : 3;

  const handleClose = () => onClose();

  const updateGlobal = (field, value) => setGlobalData(prev => ({ ...prev, [field]: value }));
  const updateResponse = (field, value) => setResponses(prev => ({ ...prev, [field]: value }));

  // --------------------------------------------------
  // VALIDATION per step (all fields required)
  // --------------------------------------------------
  const validateStep = () => {
    if (isSimple) {
      if (step === 0) {
        if (isBlank(globalData.name)) return 'Full name is required.';
        if (isBlank(globalData.email)) return 'Email is required.';
        if (isBlank(globalData.age_range)) return 'Please select your age range.';
        if (isBlank(globalData.gender)) return 'Please select your gender.';
        if (isBlank(globalData.nationality)) return 'Nationality is required.';
        if (isBlank(globalData.country)) return 'Country of residence is required.';
        if (isBlank(globalData.state)) return 'State / Region is required.';
        if (isBlank(globalData.city)) return 'City is required.';
      }
      if (step === 1) {
        for (const field of config.fields) {
          const val = responses[field.name];
          if (isBlank(val)) return `Please answer: "${field.label}"`;
        }
      }
    } else {
      if (step === 0) {
        if (isBlank(globalData.name)) return 'Full name is required.';
        if (isBlank(globalData.email)) return 'Email is required.';
      }
      if (step === 1) {
        if (isBlank(globalData.age_range)) return 'Please select your age range.';
        if (isBlank(globalData.gender)) return 'Please select your gender.';
        if (isBlank(globalData.nationality)) return 'Nationality is required.';
        if (isBlank(globalData.country)) return 'Country of residence is required.';
        if (isBlank(globalData.state)) return 'State / Region is required.';
        if (isBlank(globalData.city)) return 'City is required.';
        if (isBlank(globalData.industry)) return 'Industry / Interest is required.';
      }
      if (step === 2) {
        for (const field of config.fields) {
          const val = responses[field.name];
          if (isBlank(val)) return `Please answer: "${field.label}"`;
        }
      }
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => Math.min(s + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setError('');
    setStep(s => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }

    setIsSubmitting(true);
    setError('');
    try {
      const formPayload = {
        form_type: formType,
        name: globalData.name,
        email: globalData.email,
        age_range: globalData.age_range,
        gender: globalData.gender,
        nationality: globalData.nationality,
        country: globalData.country,
        industry: isSimple ? (responses.industry || globalData.industry || '') : globalData.industry,
        responses: {
          ...responses,
          state: globalData.state,
          city: globalData.city,
        },
      };

      // Backend handles MongoDB save + ConvertKit subscribe with tags
      const res = await axios.post(`${API}/forms`, formPayload);
      console.log('[Form] Submitted:', res.data?.convertkit_synced ? 'CK synced' : 'CK pending', 'Tags:', res.data?.convertkit_tags);

      setIsSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastStep = step === totalSteps - 1;
  const progress = isSuccess ? 100 : ((step + 1) / totalSteps) * 100;

  // --------------------------------------------------
  // Render form-specific field (with hint)
  // --------------------------------------------------
  const renderField = (field) => {
    const val = responses[field.name] || (field.type === 'checkbox' ? [] : '');
    return (
      <div key={field.name} className="mb-8">
        <label className="block font-heading text-base md:text-lg text-neutral-800 mb-1">{field.label}</label>
        {field.hint && <p className="text-neutral-400 text-sm mb-3 font-mono">{field.hint}</p>}
        {field.type === 'radio' && <RadioGroup name={field.name} options={field.options} value={val} onChange={v => updateResponse(field.name, v)} />}
        {field.type === 'checkbox' && <CheckGroup name={field.name} options={field.options} value={val} onChange={v => updateResponse(field.name, v)} />}
        {field.type === 'textarea' && (
          <textarea
            value={val}
            onChange={e => updateResponse(field.name, e.target.value)}
            placeholder={field.placeholder}
            className="editorial-input min-h-[80px] resize-none"
            data-testid={`field-${field.name}`}
          />
        )}
        {field.type === 'select' && (
          <div className="relative">
            <select
              value={val}
              onChange={e => updateResponse(field.name, e.target.value)}
              className="editorial-input appearance-none cursor-pointer pr-10"
              data-testid={`field-${field.name}`}
            >
              <option value="">Select an option</option>
              {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-400" />
          </div>
        )}
      </div>
    );
  };

  // --------------------------------------------------
  // Location fields block (reused across steps)
  // --------------------------------------------------
  const renderLocationFields = () => (
    <>
      <div className="mb-6">
        <label className="editorial-label mb-2 block">Nationality</label>
        <p className="text-neutral-400 text-xs mb-2 font-mono">Country of origin</p>
        <input type="text" value={globalData.nationality} onChange={e => updateGlobal('nationality', e.target.value)} placeholder="Example: Brazilian, French, Nigerian" className="editorial-input" data-testid="form-nationality-input" />
      </div>
      <div className="mb-6">
        <label className="editorial-label mb-2 block">Current Country</label>
        <p className="text-neutral-400 text-xs mb-2 font-mono">Where you currently live</p>
        <input type="text" value={globalData.country} onChange={e => updateGlobal('country', e.target.value)} placeholder="Example: United States, Portugal, UAE" className="editorial-input" data-testid="form-country-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="editorial-label mb-2 block">State / Region</label>
          <input type="text" value={globalData.state} onChange={e => updateGlobal('state', e.target.value)} placeholder="Example: California, Lisbon" className="editorial-input" data-testid="form-state-input" />
        </div>
        <div>
          <label className="editorial-label mb-2 block">City</label>
          <input type="text" value={globalData.city} onChange={e => updateGlobal('city', e.target.value)} placeholder="Example: Los Angeles, Porto" className="editorial-input" data-testid="form-city-input" />
        </div>
      </div>
    </>
  );

  // --------------------------------------------------
  // Step 0: Identity (name + email + intro)
  // --------------------------------------------------
  const renderIdentityStep = () => (
    <div>
      {config.intro && (
        <p className="font-heading text-xl md:text-2xl italic text-neutral-600 mb-10 leading-relaxed">
          {config.intro}
        </p>
      )}
      <div className="space-y-6">
        <div>
          <label className="editorial-label mb-2 block">Full Name</label>
          <input type="text" value={globalData.name} onChange={e => updateGlobal('name', e.target.value)} placeholder="Your full name" className="editorial-input" data-testid="form-name-input" />
        </div>
        <div>
          <label className="editorial-label mb-2 block">Email</label>
          <input type="email" value={globalData.email} onChange={e => updateGlobal('email', e.target.value)} placeholder="your@email.com" className="editorial-input" data-testid="form-email-input" />
        </div>
      </div>
    </div>
  );

  // --------------------------------------------------
  // Step 1: Profile (age, gender, location, industry)
  // --------------------------------------------------
  const renderProfileStep = () => (
    <div>
      <p className="editorial-label mb-8">About you</p>

      <div className="mb-8">
        <label className="block font-heading text-base text-neutral-800 mb-1">Age Range</label>
        <p className="text-neutral-400 text-xs mb-3 font-mono">Select your age bracket</p>
        <div className="flex flex-wrap gap-2">
          {AGE_OPTIONS.map(opt => (
            <button key={opt} type="button" onClick={() => updateGlobal('age_range', opt)}
              className={`px-5 py-2.5 border font-mono text-xs uppercase tracking-wider transition-all duration-200
                ${globalData.age_range === opt ? 'bg-black text-white border-black' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}
              data-testid={`age-${opt}`}
            >{opt}</button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="block font-heading text-base text-neutral-800 mb-1">Gender</label>
        <p className="text-neutral-400 text-xs mb-3 font-mono">How do you identify?</p>
        <div className="flex flex-wrap gap-2">
          {GENDER_OPTIONS.map(opt => (
            <button key={opt} type="button" onClick={() => updateGlobal('gender', opt)}
              className={`px-4 py-2.5 border font-mono text-xs uppercase tracking-wider transition-all duration-200
                ${globalData.gender === opt ? 'bg-black text-white border-black' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}
              data-testid={`gender-${opt.toLowerCase().replace(/\s+/g, '-')}`}
            >{opt}</button>
          ))}
        </div>
      </div>

      {renderLocationFields()}

      <div>
        <label className="editorial-label mb-2 block">Industry / Interest</label>
        <p className="text-neutral-400 text-xs mb-2 font-mono">What space, industry, or type of business are you connected to?</p>
        <input type="text" value={globalData.industry} onChange={e => updateGlobal('industry', e.target.value)} placeholder="Example: health, finance, beauty, education, digital products" className="editorial-input" data-testid="form-industry-input" />
      </div>
    </div>
  );

  // --------------------------------------------------
  // Step 2: Form-specific questions
  // --------------------------------------------------
  const renderQuestionsStep = () => (
    <div>
      <p className="editorial-label mb-8">Your answers</p>
      {config.fields.map(renderField)}
    </div>
  );

  // --------------------------------------------------
  // Waitlist step 0: identity + profile in one
  // --------------------------------------------------
  const renderWaitlistIdentityStep = () => (
    <div>
      <div className="space-y-6 mb-10">
        <div>
          <label className="editorial-label mb-2 block">Full Name</label>
          <input type="text" value={globalData.name} onChange={e => updateGlobal('name', e.target.value)} placeholder="Your full name" className="editorial-input" data-testid="form-name-input" />
        </div>
        <div>
          <label className="editorial-label mb-2 block">Email</label>
          <input type="email" value={globalData.email} onChange={e => updateGlobal('email', e.target.value)} placeholder="your@email.com" className="editorial-input" data-testid="form-email-input" />
        </div>
      </div>

      <div className="mb-8">
        <label className="block font-heading text-base text-neutral-800 mb-1">Age Range</label>
        <p className="text-neutral-400 text-xs mb-3 font-mono">Select your age bracket</p>
        <div className="flex flex-wrap gap-2">
          {AGE_OPTIONS.map(opt => (
            <button key={opt} type="button" onClick={() => updateGlobal('age_range', opt)}
              className={`px-5 py-2.5 border font-mono text-xs uppercase tracking-wider transition-all duration-200
                ${globalData.age_range === opt ? 'bg-black text-white border-black' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}
              data-testid={`age-${opt}`}
            >{opt}</button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <label className="block font-heading text-base text-neutral-800 mb-1">Gender</label>
        <p className="text-neutral-400 text-xs mb-3 font-mono">How do you identify?</p>
        <div className="flex flex-wrap gap-2">
          {GENDER_OPTIONS.map(opt => (
            <button key={opt} type="button" onClick={() => updateGlobal('gender', opt)}
              className={`px-4 py-2.5 border font-mono text-xs uppercase tracking-wider transition-all duration-200
                ${globalData.gender === opt ? 'bg-black text-white border-black' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`}
              data-testid={`gender-${opt.toLowerCase().replace(/\s+/g, '-')}`}
            >{opt}</button>
          ))}
        </div>
      </div>

      {renderLocationFields()}
    </div>
  );

  // --------------------------------------------------
  // Waitlist step 1: form questions
  // --------------------------------------------------
  const renderWaitlistQuestionsStep = () => (
    <div>
      <p className="editorial-label mb-8">Almost there</p>
      {config.fields.map(renderField)}
    </div>
  );

  // --------------------------------------------------
  // Render current step
  // --------------------------------------------------
  const renderCurrentStep = () => {
    if (isSimple) {
      return step === 0 ? renderWaitlistIdentityStep() : renderWaitlistQuestionsStep();
    }
    if (step === 0) return renderIdentityStep();
    if (step === 1) return renderProfileStep();
    return renderQuestionsStep();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)' }}
          onClick={handleClose}
          data-testid={`form-modal-${formType.toLowerCase()}`}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="bg-white w-full max-w-xl max-h-[90vh] overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
            data-testid="form-modal-panel"
          >
            {/* Progress bar */}
            <div className="h-0.5 bg-neutral-100 sticky top-0 z-10">
              <motion.div
                className="h-full bg-editorial-red"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>

            {/* Close */}
            <button
              onClick={handleClose}
              className="absolute top-5 right-5 p-2 hover:opacity-60 transition-opacity z-10"
              data-testid="form-modal-close"
            >
              <X size={20} strokeWidth={1} />
            </button>

            <div className="p-8 md:p-12">
              {!isSuccess && (
                <div className="mb-8">
                  <span className="editorial-label text-editorial-red">{config.title}</span>
                </div>
              )}

              {isSuccess ? (
                <SuccessScreen />
              ) : (
                <>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      variants={stepAnim}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.25 }}
                    >
                      {renderCurrentStep()}
                    </motion.div>
                  </AnimatePresence>

                  {error && (
                    <p className="text-editorial-red text-sm font-mono mt-4" data-testid="form-error">{error}</p>
                  )}

                  <div className="flex items-center justify-between mt-10 pt-6 border-t border-neutral-100">
                    {step > 0 ? (
                      <button type="button" onClick={handleBack}
                        className="font-mono text-xs uppercase tracking-wider text-neutral-400 hover:text-black transition-colors"
                        data-testid="form-back-btn"
                      >Back</button>
                    ) : <div />}

                    {isLastStep ? (
                      <button type="button" onClick={handleSubmit} disabled={isSubmitting}
                        className="editorial-btn disabled:opacity-40 min-w-[160px]"
                        data-testid="form-submit-btn"
                      >{isSubmitting ? 'Submitting...' : 'Submit'}</button>
                    ) : (
                      <button type="button" onClick={handleNext}
                        className="editorial-btn min-w-[160px]"
                        data-testid="form-next-btn"
                      >Continue <ArrowRight size={14} className="inline ml-2" /></button>
                    )}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EditorialFormModal;
